import { Box, Stack, Typography, styled } from '@mui/material';
import React, { FC, useEffect, useRef, useState } from 'react';
import useCaseResults from '../../api/useCaseResults';
import {
  CaseNote,
  CaseResponsePayloadDTO,
  CaseResultDetails,
  EconsultProviderTypes,
  EconsultStatusTypes,
  EconsultUser,
  EconsultUserRoles,
  NoteActionTypes,
  NoteTypes,
  EconsultUtils
} from '@oh-vcp/components-common';
import {
  EconsultCaseInboxFilter,
  EconsultCaseInboxItem,
  EconsultCaseInboxPagination,
  EconsultCaseSearchBox,
  EconsultDelegatorSelector,
  EconsultProgramSelector,
  useConsultantService
} from '@oh-vcp/components-web';
import { useNavigate, useParams } from 'react-router-dom';
import { useEconsultCaseDetailsContext } from '@oh-vcp/components-web/src/econsultCaseDetailsContext/EconsultCaseDetailsContext';
import { useEconsultCaseResultContext } from '@oh-vcp/components-web/src/econsultCaseResultContext/EconsultCaseResultContext';
import { useTranslation } from 'react-i18next';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { DisplayMobile, Notification } from '@oh-vcp/components-ui';
import useCaseSearch from '../../api/useCaseSearch';
import EconsultDraftLostWarningDialog from '@oh-vcp/components-web/src/econsultDraftLostWarningDialog/EconsultDraftLostWarningDialog';
import { CaseFolderPage } from '@oh-vcp/components-common/src/models/case/CaseDetails';
const isIpad = EconsultUtils.isIPad();
const EconsultCaseInboxStack = styled(Box)(({ theme }) => ({
  overflowY: 'auto',
  [theme.breakpoints.up('xs')]: {
    height: 'calc(99vh - 20rem)'
  },
  [theme.breakpoints.up('lg')]: {
    height: 'calc(100vh - 16rem)'
  }
}));

interface EconsultCaseInboxProps {
  isDraftChanged: () => boolean | false;
  setUpdatedDraftNote: (updDraftNote: CaseNote | undefined) => void;
  setUpdatedCaseDetails: (updCaseDetails: CaseResultDetails | undefined) => void;
  getUpdatedDraftNote: () => CaseNote | undefined;
}

const EconsultCaseInboxContainerStack = styled(Stack)(({ theme }) => ({
  borderRightWidth: 1,
  borderRightColor: theme.palette.base.grey6,
  borderRightStyle: 'solid',
  paddingRight: 5,
  [theme.breakpoints.up('xs')]: {
    width: '99vw'
  },
  [theme.breakpoints.up('md')]: {
    width: '99vw'
  },
  [theme.breakpoints.up('lg')]: {
    width: 450
  }
}));

const EconsultCaseInbox: FC<EconsultCaseInboxProps> = ({
  isDraftChanged,
  setUpdatedDraftNote,
  setUpdatedCaseDetails,
  getUpdatedDraftNote
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { dashboardUserRole } = useEconsultCaseResultContext();
  const {
    caseDetails,
    setCaseDetails,
    abortAllUploads,
    currentFolderUrl,
    caseFolderPages,
    setCaseFolderPages
  } = useEconsultCaseDetailsContext();
  const { data: consultantsData } = useConsultantService();
  const { providerType, statusType } = useParams();
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [subFolder, setSubFolder] = useState<string | undefined>(undefined);
  const [resultsToDisplay, setResultsToDisplay] = useState<CaseResponsePayloadDTO | undefined>();
  const [delegatorId, setDelegatorId] = useState<string | undefined>(undefined);
  const [myConsultants, setMyConsultants] = useState<EconsultUser[]>([]);
  const [selectedProgramName, setSelectedProgramName] = useState<string | undefined>(undefined);
  const [hasCasesReadyToBeAssigned, setHasCasesReadyToBeAssigned] = useState<boolean>(false);
  const [isDraftWarningDialogOpen, setIsDraftWarningDialogOpen] = useState<boolean>(false);
  const inboxStackRef = useRef<HTMLDivElement>(null);
  const prevTotalCount = useRef<number>();
  const prevPage = useRef<number>(1);
  const numToDisplay = 25;
  const scrollToTop = () => {
    if (inboxStackRef.current) {
      inboxStackRef.current.scrollTo({ top: 0 });
    }
  };

  const removeCaseFolderPage = (type: string | undefined, statusType: string | undefined) => {
    if (caseFolderPages) {
      return caseFolderPages.filter(
        (p) =>
          !(
            (type === undefined &&
              p.providerType === undefined &&
              statusType === undefined &&
              p.statusType === undefined) ||
            (p.providerType === type && p.statusType === statusType)
          )
      );
    }
    return [];
  };
  const addCaseFolderPage = (pageNum: number) => {
    const newCseFolderPages = removeCaseFolderPage(providerType, statusType);
    console.log(`addCaseFolderPage - removeCaseFolderPage: ${JSON.stringify(newCseFolderPages)}`);
    const currentCaseFolderPage = {
      providerType: providerType,
      statusType: statusType,
      page: pageNum
    };
    if (newCseFolderPages) {
      return [...newCseFolderPages, currentCaseFolderPage];
    } else {
      return [currentCaseFolderPage];
    }
  };
  const getCurCaseFolderPage = (type: string | undefined, statusType: string | undefined) => {
    if (caseFolderPages) {
      const myfolderPage = caseFolderPages.filter(
        (p) =>
          (type === undefined &&
            p.providerType === undefined &&
            statusType === undefined &&
            p.statusType === undefined) ||
          (p.providerType === type && p.statusType === statusType)
      );
      return caseFolderPages && caseFolderPages.length > 0 ? myfolderPage[0] : undefined;
    }
    return undefined;
  };
  const toPageN = () => {
    const storedPage = getCurCaseFolderPage(providerType, statusType);
    console.log(`toPageN - currentPageNumber: ${JSON.stringify(storedPage)}`);
    if (storedPage && storedPage.page) {
      return storedPage.page;
    }
    return 1;
  };

  useEffect(() => {
    setCurrentPage(toPageN());
  }, []);

  const { data: caseResults } = useCaseResults(
    currentPage,
    numToDisplay,
    providerType?.toUpperCase() ||
      dashboardUserRole ||
      EconsultProviderTypes.consultant.toUpperCase(),
    statusType !== EconsultStatusTypes.all ? statusType?.toUpperCase() : undefined,
    subFolder,
    delegatorId,
    selectedProgramName !== '0' ? selectedProgramName : undefined
  );
  const { data: searchResults } = useCaseSearch(
    providerType?.toLowerCase() === 'search',
    statusType,
    currentPage,
    numToDisplay
  );

  useEffect(() => {
    setSelectedProgramName(undefined);
  }, [currentFolderUrl]);

  useEffect(() => {
    if (prevTotalCount.current) {
      if (
        resultsToDisplay &&
        resultsToDisplay.totalCount &&
        prevTotalCount.current > resultsToDisplay.totalCount
      ) {
        prevTotalCount.current = resultsToDisplay.totalCount;
        if (statusType === 'DRAFT' && currentPage === prevPage.current) {
          setCurrentPage(toPageN());
          scrollToTop();
        }
      }
    } else {
      prevTotalCount.current = resultsToDisplay?.totalCount;
    }
    prevPage.current = currentPage;
    const cases = resultsToDisplay?.data?.find((r) => {
      if (
        r.programId &&
        r.userRole === NoteTypes.triageAdmin &&
        !r.isConsultActioned &&
        (r.lastAction === NoteActionTypes.send ||
          r.lastAction === NoteActionTypes.assign ||
          r.lastAction === NoteActionTypes.unassign ||
          (r.lastAction === NoteActionTypes.addNote && r.nextActionBy === NoteTypes.triageAdmin) ||
          (r.lastAction === NoteActionTypes.addNote &&
            r.lastActionBy === NoteTypes.referrer &&
            r.nextActionBy === NoteTypes.consultant))
      ) {
        return r;
      }
      return undefined;
    });
    if (cases) {
      setHasCasesReadyToBeAssigned(true);
    } else {
      setHasCasesReadyToBeAssigned(false);
    }
  }, [resultsToDisplay]);
  useEffect(() => {
    const updateDelegators = async () => {
      if (providerType?.toUpperCase() === EconsultUserRoles.consultant) {
        let consultants = consultantsData || [];
        consultants = consultants
          .filter((c) => c.roles?.econCaseConsultOn)
          .sort((a, b) => a.firstName.localeCompare(b.firstName));
        setMyConsultants([
          {
            firstName: 'all specialists I can act on behalf of',
            userId: 0
          },
          ...consultants
        ]);
      } else if (providerType?.toUpperCase() === EconsultUserRoles.referrer) {
        let consultants = consultantsData || [];
        consultants = consultants
          .filter((c) => c.roles?.econCaseCreate)
          .sort((a, b) => a.firstName.localeCompare(b.firstName));
        setMyConsultants([
          {
            firstName: 'all requesters I can act on behalf of',
            userId: 0
          },
          ...consultants
        ]);
      }
    };

    updateDelegators();
  }, [providerType, consultantsData]);
  useEffect(() => {
    if (providerType?.toLowerCase() === 'search') {
      setResultsToDisplay(searchResults);
    } else {
      setResultsToDisplay(caseResults);
    }
  }, [caseResults, searchResults, setCaseDetails, providerType]);
  useEffect(() => {
    setSubFolder('');
  }, [statusType]);
  useEffect(() => {
    setCurrentPage(toPageN());
    scrollToTop();
  }, [providerType, statusType]);

  const onNextClicked = () => {
    if (resultsToDisplay && currentPage * numToDisplay < resultsToDisplay?.totalCount) {
      setCurrentPage(currentPage + 1);
      setCaseFolderPages(addCaseFolderPage(currentPage + 1));
      scrollToTop();
    }
  };
  const onPrevClicked = () => {
    if (resultsToDisplay && currentPage > 1) {
      setCurrentPage(currentPage - 1);
      setCaseFolderPages(addCaseFolderPage(currentPage - 1));
      scrollToTop();
    }
  };
  const getInboxTitle = () => {
    if (providerType === EconsultProviderTypes.consultant) {
      switch (statusType) {
        case EconsultStatusTypes.all:
          return t('Case.inbox.allCasesSubmittedToMe');
        case EconsultStatusTypes.needsAction:
          if (myConsultants.length > 1 || hasCasesReadyToBeAssigned) {
            return t('Case.inbox.casesThatNeedMyAttentionFor');
          }
          return t('Case.inbox.casesThatNeedMyAttention');
        case EconsultStatusTypes.waiting:
          return t('Case.inbox.casesWaitingForMoreInfo');
        case EconsultStatusTypes.provided:
          return t('Case.inbox.casesWithProvidedConsult');
        case EconsultStatusTypes.declined:
          return t('Case.inbox.casesWithReturnedConsult');
      }
    } else if (providerType === EconsultProviderTypes.referrer) {
      switch (statusType) {
        case EconsultStatusTypes.all:
          return t('Case.inbox.allCasesSubmittedByMe');
        case EconsultStatusTypes.needsAction:
          if (myConsultants.length > 1 || hasCasesReadyToBeAssigned) {
            return t('');
          }
          return t('Case.inbox.casesThatNeedMyAttention');
        case EconsultStatusTypes.waiting:
          return t('Case.inbox.casesWaitingForResponse');
        case EconsultStatusTypes.completed:
          return t('Case.inbox.completedCases');
        case EconsultStatusTypes.cancelled:
          return t('Case.inbox.cancelledCases');
        case EconsultStatusTypes.draft:
          return t('Case.inbox.draftCases');
      }
    }

    return '';
  };
  const handleOnFilterSelected = (value: string) => {
    navigate(`/case/${providerType}/${statusType}`);
    if (value === '') {
      setSubFolder(undefined);
    } else {
      setSubFolder(value);
    }
  };
  const handleGoBack = () => {
    if (
      caseDetails &&
      caseDetails.caseId === EconsultUtils.getCaseIdOnCurrentPage() &&
      isDraftChanged()
    ) {
      setIsDraftWarningDialogOpen(true);
    } else {
      navigate('/');
    }
  };
  const handleDelegatorUserSelected = (userId: string) => {
    setDelegatorId(userId === '0' ? undefined : userId);
  };

  const isDuplicateCase = (caseId: number) => {
    const duplicateCases = resultsToDisplay?.data?.filter((c) => c.caseId === caseId);
    if (duplicateCases && duplicateCases.length > 1) {
      return true;
    }
    return false;
  };
  const isSameRole = (role: string) => {
    if (providerType && role && role.toUpperCase().indexOf(providerType.toUpperCase()) !== -1) {
      return true;
    }
    return false;
  };
  const isTab = window.innerWidth < 960;
  const isMedian = window.innerWidth < 1280;
  const isSmallScreen = window.innerWidth < 601;
  const pageAlign =
    EconsultUtils.isMobileDevice() || isTab || EconsultUtils.isIPad() ? 'left' : 'right';
  const pagePaddingLeft =
    EconsultUtils.isMobileDevice() && !EconsultUtils.isIPad()
      ? 16
      : isTab || EconsultUtils.isIPad()
      ? 24
      : 0;
  const containerWidth =
    (EconsultUtils.isMobileDevice() && !EconsultUtils.isIPad()) || isSmallScreen
      ? '70%'
      : 'inherit';
  const onDraftWarningDialogCancelled = () => {
    setIsDraftWarningDialogOpen(false);
    setUpdatedDraftNote(undefined);
    setUpdatedCaseDetails(undefined);
    abortAllUploads();
    navigate('/');
  };
  const onDraftWarningDialogConfirmed = () => {
    setIsDraftWarningDialogOpen(false);
  };
  const xsHeight =
    isMedian && (hasCasesReadyToBeAssigned || (myConsultants && myConsultants.length > 1))
      ? 'calc(99vh - 21.6rem)'
      : isMedian
      ? 'calc(99vh - 20rem)'
      : 'calc(100vh - 16rem)';
  const containerHeight =
    EconsultUtils.isMobileDevice() || isSmallScreen
      ? EconsultUtils.isChromeOnAndroid()
        ? 'calc(100vh - 9.6rem)'
        : 'calc(100vh - 10.5rem)'
      : 'inherit';

  const onGroupChanged = (name: string | undefined) => {
    setCurrentPage(1);
    setSelectedProgramName(name);
    navigate(`/case/${providerType}/${statusType}`);
  };

  //setTimeout(toPageN, 100);
  let resultDisplayTrimed: CaseResultDetails[] = [];
  resultsToDisplay?.data?.map((details: CaseResultDetails) => (
    (providerType?.toLowerCase() === 'search' || (!isDuplicateCase(details.caseId) || isSameRole(details.userRole))) ? resultDisplayTrimed.push(details) :
    console.log( `${providerType} , ${details.caseId} is duplicate, removed` )));
  return (
    <>
      <EconsultCaseInboxContainerStack direction="column" sx={{ height: containerHeight }}>
        <DisplayMobile>
          <Box mt={2}>
            <ArrowBackIcon onClick={handleGoBack} />
          </Box>
        </DisplayMobile>
        <Typography variant="body1" color="base.grey4" ml={2} mt={2}>
          {getInboxTitle()}
        </Typography>
        {providerType?.toLowerCase() === 'search' ? (
          <Box ml={1}>
            <EconsultCaseSearchBox
              setResultsToDisplay={setResultsToDisplay}
            />
          </Box>
        ) : (
          <Box sx={{ width: containerWidth }}>
            {hasCasesReadyToBeAssigned || selectedProgramName ? (
              <Box mt={1} px={1}>
                <EconsultProgramSelector
                  options={resultsToDisplay?.programs || []}
                  onSelected={(name) => onGroupChanged(name)}
                  selectedProgramName={selectedProgramName || '0'}
                />
              </Box>
            ) : (
              <>
                {myConsultants && myConsultants.length > 2 && (
                  <Box mt={1} px={1}>
                    <EconsultDelegatorSelector
                      userId={delegatorId || '0'}
                      onUserSelected={handleDelegatorUserSelected}
                      options={myConsultants}
                    />
                  </Box>
                )}
              </>
            )}
          </Box>
        )}
        {!hasCasesReadyToBeAssigned && !selectedProgramName && (
          <EconsultCaseInboxFilter onSelected={handleOnFilterSelected} />
        )}
        {resultsToDisplay && resultsToDisplay.totalCount > 0 ? (
          <>
            <EconsultCaseInboxStack ref={inboxStackRef} sx={{ height: xsHeight }}>
              {resultDisplayTrimed?.map((details: CaseResultDetails) => (
                <EconsultCaseInboxItem
                  caseDetails={details}
                  key={details.caseId}
                  isDuplicateCase={isDuplicateCase}
                  isDraftChanged={isDraftChanged}
                  setUpdatedDraftNote={setUpdatedDraftNote}
                  setUpdatedCaseDetails={setUpdatedCaseDetails}
                  getUpdatedDraftNote={getUpdatedDraftNote}
                />
              ))}
            </EconsultCaseInboxStack>
            <Stack
              direction="row"
              justifyContent={pageAlign}
              style={{ paddingLeft: pagePaddingLeft }}
              pt={4}>
              <EconsultCaseInboxPagination
                currentPage={currentPage}
                total={resultsToDisplay?.totalCount}
                numToDisplay={25}
                onNext={onNextClicked}
                onPrev={onPrevClicked}
              />
            </Stack>
          </>
        ) : (
          resultsToDisplay && (
            <Typography variant="body2" color="base.grey4" ml={2} mt={2}>
              {t('Case.inbox.noCasesSearch')}
            </Typography>
          )
        )}
      </EconsultCaseInboxContainerStack>
      <EconsultDraftLostWarningDialog
        isOpen={isDraftWarningDialogOpen}
        onLeave={onDraftWarningDialogCancelled}
        onStay={onDraftWarningDialogConfirmed}
      />
    </>
  );
};

export default EconsultCaseInbox;
